import { t } from 'i18next';
import { I18N_KEYS } from '@/language/keys';
import { TrackTypeEnum, type TrackType } from '@/hooks/useAnalytics';
import type { ConnectResult } from '../Views/FlowView';
import type { SnackbarData } from '@/state/global.state';
import type { ApiObject } from '@/types/analytics.types';

type UpdateSnackbarData = (data: Partial<SnackbarData>) => void;
type TrackFn = (event: string, data: ApiObject | null, type?: TrackType) => void;

/** Silent on full success; info on partial; error when the primary itself couldn't connect. */
export function surfaceMultiConnectResult(
  result: ConnectResult,
  totalSources: number,
  updateSnackbarData: UpdateSnackbarData,
): void {
  if (result.primary !== 'ok') {
    updateSnackbarData({
      text: t(I18N_KEYS.RECIPE_MAIN.FLOW.MULTI_CONNECT.INCOMPATIBLE_TARGET),
      isOpen: true,
      icon: null,
      severity: 'error',
    });
    return;
  }
  const connected = result.siblings.filter((s) => s.status === 'ok').length + 1;
  if (connected === totalSources) return;
  updateSnackbarData({
    text: t(I18N_KEYS.RECIPE_MAIN.FLOW.MULTI_CONNECT.PARTIAL_SUCCESS, {
      connected,
      total: totalSources,
      skipped: totalSources - connected,
    }),
    isOpen: true,
    icon: null,
    severity: 'info',
  });
}

type MultiConnectStatus = 'primary_failed' | 'full_success' | 'partial_success';

function getMultiConnectStatus(
  result: ConnectResult,
  connectedCount: number,
  totalSources: number,
): MultiConnectStatus {
  if (result.primary !== 'ok') return 'primary_failed';
  if (connectedCount === totalSources) return 'full_success';
  return 'partial_success';
}

/** Fires the BI `edge_connection_completed` event for a multi-connect attempt. */
export function trackMultiConnectResult(args: {
  result: ConnectResult;
  entry: 'drop_on_target' | 'drop_on_canvas';
  siblingCount: number;
  track: TrackFn;
}): void {
  const { result, entry, siblingCount, track } = args;
  const totalSources = siblingCount + 1;
  const connectedCount = result.primary === 'ok' ? result.siblings.filter((s) => s.status === 'ok').length + 1 : 0;
  track(
    'edge_connection_completed',
    {
      mode: 'multi',
      entry,
      status: getMultiConnectStatus(result, connectedCount, totalSources),
      sibling_count: siblingCount,
      total_sources: totalSources,
      connected_count: connectedCount,
      skipped_count: totalSources - connectedCount,
    },
    TrackTypeEnum.BI,
  );
}
